import { type ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router';

import useAuthorization from '@@auth/hooks/useAuthorization';
import { type NavigationItem } from '@@config';
import { Error401 } from '@@containers/App/Error401';
import { Error403 } from '@@containers/App/Error403';
import { writeLastRoutePathToStorage } from '@@containers/LastRouteTracker/utils';
import { login } from '@@router/login';
import { useSettingsStore } from '@@scripts/store/settings/useSettingsStore';
import { useAuthStore, useIsAuthenticated } from '@@scripts/store/useAuthStore';

export type Props = Partial<NavigationItem> & {
    children?: ReactNode | ((props: Omit<Props, 'children' | 'authorize'>) => ReactNode);
    path?: string;
};

const PrivateRoute = ({ children, authorize, betaFeature, ...props }: Props) => {
    const { isAuthorized } = useAuthorization();
    const isAuthenticated = useIsAuthenticated();
    const accessToken = useAuthStore((state) => state.accessToken);
    const betaFeaturesEnabled = useSettingsStore((state) => state.betaFeaturesEnabled);
    const arePreferencesLoaded = useSettingsStore((state) => state.arePreferencesLoaded);

    if (!isAuthorized(authorize)) {
        return <Error403 />;
    }

    if (betaFeature && arePreferencesLoaded && !betaFeaturesEnabled) {
        return <Navigate to="/404" />;
    }

    // Allow the user to access the route if authentication is valid
    if (isAuthenticated) {
        if (children) {
            return typeof children === 'function' ? children(props) : children;
        }

        return <Outlet />;
    }

    // In order to avoid using acceptance environments when not necessary
    // We revert to default ones when token expires
    if (accessToken && !isAuthenticated) {
        localStorage.removeItem('CDII_API');
    }

    // Remember the requested route synchronously: the login redirect can unload the page before
    // the `useLastRouteTracker` effect has stored it, and the user would land on `/` after login
    writeLastRoutePathToStorage(window.location.pathname + window.location.search);

    // Trigger a login process if authentication fails
    login();

    return <Error401 />;
};

export default PrivateRoute;
